import type { ConvRegion } from '@orborix/shared-types';

/**
 * Helpers de presentación del feature "Escuchar" (Fase 19.D): formato de tiempo
 * y metadata de las voces reales (sólo 2 hoy: `adam-en-us`, `martin-es-latam`).
 *
 * La metadata de voz (nombre/avatar) NO viene del backend — el capítulo expone
 * `voiceSlug` y el acento. Acá la mapeamos para el `VoiceSheet`/chips. Si
 * aparece un slug desconocido, derivamos un nombre del slug; idioma y bandera
 * salen del `available[]` del endpoint (antes toda voz en inglés desconocida
 * salía con 🇺🇸, también las británicas — encargo 111).
 */

export interface VoiceMeta {
  name: string;
  /** 'en' | 'es' — idioma de la narración. */
  lang: string;
  region: string;
  tag: string;
  /** Color del avatar (hex). */
  color: string;
  initial: string;
  flag: string;
}

const VOICE_META: Record<string, VoiceMeta> = {
  'adam-en-us': {
    name: 'Adam',
    lang: 'en',
    region: 'EE. UU.',
    tag: 'Cálida',
    color: '#7cc4ff',
    initial: 'A',
    flag: '🇺🇸',
  },
  'martin-es-latam': {
    name: 'Martín',
    lang: 'es',
    region: 'Latinoamérica',
    tag: 'Clara',
    color: '#ff9d6c',
    initial: 'M',
    flag: '🌎',
  },
};

/** Bandera y región legible de cada acento de narración (`listen_renditions.accent`). */
const NARRATION_ACCENT_DISPLAY: Record<string, { flag: string; region: string }> = {
  us: { flag: '🇺🇸', region: 'EE. UU.' },
  uk: { flag: '🇬🇧', region: 'Reino Unido' },
  au: { flag: '🇦🇺', region: 'Australia' },
  latam: { flag: '🌎', region: 'Latinoamérica' },
};

/** Bandera de un acento de narración; `null` si no se conoce (no se adivina). */
export function narrationFlag(accent: string | null | undefined): string | null {
  return (accent && NARRATION_ACCENT_DISPLAY[accent]?.flag) ?? null;
}

/** Acento de narración que corresponde a cada «Tu inglés». */
export const NARRATION_ACCENT_FOR_REGION: Record<ConvRegion, string> = {
  american: 'us',
  british: 'uk',
  australian: 'au',
};

/**
 * Metadata de una voz por slug; para un slug desconocido deriva el nombre del
 * slug y toma idioma y bandera de lo que dice el capítulo (`lang`, `accent`).
 */
export function voiceMetaFor(slug: string, lang: string, accent: string | null): VoiceMeta {
  const hit = VOICE_META[slug];
  if (hit) return hit;
  const head = slug.split('-')[0] ?? slug;
  const name = head.charAt(0).toUpperCase() + head.slice(1);
  const display = accent ? NARRATION_ACCENT_DISPLAY[accent] : undefined;
  return {
    name,
    lang,
    region: display?.region ?? '',
    tag: '',
    color: '#a78bfa',
    initial: name.charAt(0).toUpperCase(),
    flag: display?.flag ?? (lang === 'es' ? '🌎' : '🌐'),
  };
}

/** `m:ss` (minutos sin pad, segundos con pad de 2). Igual que el prototipo. */
export function fmtClock(sec: number): string {
  const safe = Number.isFinite(sec) && sec > 0 ? sec : 0;
  const m = Math.floor(safe / 60);
  const s = Math.floor(safe % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export const LANG_LABEL: Record<string, string> = { en: 'Inglés', es: 'Español' };
